import React, { useState, useEffect } from 'react';
import { FormField, TextInput, SelectInput, NavButtons } from './QuoteFormField';

const ENTITY_TYPES = ['LLC', 'Corporation', 'S-Corp', 'Partnership', 'Sole Proprietor', 'Non-Profit', 'Other'];

const ZIP_CITY_MAP = {
  '90703': 'Cerritos',
};

function validate(form, isCommercial) {
  const errors = {};

  if (!form.first_name.trim()) errors.first_name = 'First name is required.';
  if (!form.last_name.trim()) errors.last_name = 'Last name is required.';
  if (!form.email.trim()) {
    errors.email = 'Email is required.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = 'Please enter a valid email address.';
  }
  if (!form.phone.trim()) {
    errors.phone = 'Phone number is required.';
  } else if (form.phone.replace(/\D/g, '').length !== 10) {
    errors.phone = 'Please enter a valid 10-digit US phone number.';
  }
  if (!form.address_street.trim()) errors.address_street = 'Street address is required.';
  if (!form.address_city.trim()) errors.address_city = 'City is required.';
  if (!form.address_state.trim()) errors.address_state = 'State is required.';
  if (!form.address_zip.trim()) {
    errors.address_zip = 'ZIP code is required.';
  } else if (!/^\d{5}$/.test(form.address_zip)) {
    errors.address_zip = 'ZIP code must be 5 digits.';
  }

  if (!isCommercial) {
    if (!form.date_of_birth) {
      errors.date_of_birth = 'Date of birth is required.';
    } else {
      const dob = new Date(form.date_of_birth);
      const now = new Date();
      const age = (now - dob) / (1000 * 60 * 60 * 24 * 365.25);
      if (age < 16) errors.date_of_birth = 'Applicant must be at least 16 years old.';
      if (dob >= now) errors.date_of_birth = 'Date of birth must be in the past.';
    }
  } else {
    if (!form.business_legal_name.trim()) errors.business_legal_name = 'Business legal name is required.';
    if (form.business_fein && !/^\d{2}-\d{7}$/.test(form.business_fein)) {
      errors.business_fein = 'FEIN must be in format XX-XXXXXXX.';
    }
  }

  return errors;
}

export default function QuoteStepApplicant({ form, isCommercial, onChange, onNext, onBack }) {
  const [errors, setErrors] = useState({});

  // Pre-fill ZIP from Step 1's estimate_zip and auto-resolve city for known ZIPs
  useEffect(() => {
    const updates = {};
    if (form.estimate_zip && !form.address_zip) {
      updates.address_zip = form.estimate_zip;
    }
    const zipToUse = updates.address_zip || form.address_zip;
    if (zipToUse && ZIP_CITY_MAP[zipToUse] && !form.address_city) {
      updates.address_city = ZIP_CITY_MAP[zipToUse];
    }
    if (Object.keys(updates).length > 0) {
      onChange(updates);
    }
  }, []); // run once on mount

  const handleNext = () => {
    const errs = validate(form, isCommercial);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    onNext();
  };

  const field = (name) => ({
    value: form[name],
    onChange: (val) => {
      onChange({ [name]: val });
      if (errors[name]) setErrors(prev => { const e = { ...prev }; delete e[name]; return e; });
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2">Your Information</h2>
        <p className="text-gray-500">Please fill out all required fields.</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField label="First Name" required error={errors.first_name}>
          <TextInput {...field('first_name')} placeholder="Maria" />
        </FormField>
        <FormField label="Last Name" required error={errors.last_name}>
          <TextInput {...field('last_name')} placeholder="Santos" />
        </FormField>
      </div>

      <FormField label="Email Address" required error={errors.email}>
        <TextInput {...field('email')} type="email" placeholder="maria@email.com" />
      </FormField>

      <FormField label="Phone Number" required error={errors.phone}>
        <TextInput {...field('phone')} type="tel" placeholder="(562) 555-1234" />
      </FormField>

      {!isCommercial && (
        <FormField label="Date of Birth" required error={errors.date_of_birth}>
          <TextInput {...field('date_of_birth')} type="date" />
        </FormField>
      )}

      {isCommercial && (
        <div className="space-y-4 pt-2">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide border-b pb-2">Business Information</h3>
          <FormField label="Business Legal Name" required error={errors.business_legal_name}>
            <TextInput {...field('business_legal_name')} placeholder="Santos Enterprises LLC" />
          </FormField>
          <FormField label="DBA / Trade Name" error={errors.business_dba}>
            <TextInput {...field('business_dba')} placeholder="Optional" />
          </FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="FEIN / Tax ID" error={errors.business_fein}>
              <TextInput {...field('business_fein')} placeholder="12-3456789" />
            </FormField>
            <FormField label="Year Established" error={errors.business_year_est}>
              <TextInput {...field('business_year_est')} type="number" placeholder="2010" />
            </FormField>
          </div>
          <FormField label="Legal Entity Type" error={errors.business_entity_type}>
            <SelectInput
              {...field('business_entity_type')}
              options={ENTITY_TYPES}
              placeholder="Select entity type"
            />
          </FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Business Phone" error={errors.business_phone}>
              <TextInput {...field('business_phone')} type="tel" placeholder="(562) 555-0100" />
            </FormField>
            <FormField label="Business Website" error={errors.business_website}>
              <TextInput {...field('business_website')} placeholder="www.example.com (optional)" />
            </FormField>
          </div>
          <FormField label="Description of Business Operations" required error={errors.business_description}>
            <textarea
              value={form.business_description}
              onChange={e => onChange({ business_description: e.target.value })}
              rows={3}
              placeholder="Describe what your business does..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent resize-none"
            />
          </FormField>
        </div>
      )}

      <div className="space-y-4 pt-2">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide border-b pb-2">
          {isCommercial ? 'Mailing Address' : 'Address'}
        </h3>
        <FormField label="Street Address" required error={errors.address_street}>
          <TextInput {...field('address_street')} placeholder="123 Main St" />
        </FormField>
        <div className="grid grid-cols-2 gap-4">
          <FormField label="City" required error={errors.address_city}>
            <TextInput {...field('address_city')} placeholder="Cerritos" />
          </FormField>
          <FormField label="State" required error={errors.address_state}>
            <TextInput {...field('address_state')} placeholder="CA" maxLength={2} />
          </FormField>
        </div>
        <FormField label="ZIP Code" required error={errors.address_zip}>
          <TextInput {...field('address_zip')} placeholder="90703" maxLength={5} />
        </FormField>
      </div>

      <NavButtons onBack={onBack} onNext={handleNext} />
    </div>
  );
}
